import type { PublicRecord } from "@hydeck/mdt-contracts";
import { createSignal, onMount, Show } from "solid-js";
import { runtimeConfig } from "../config";
import { apiRequest } from "../core/api";

export function PublicRecordView(props: { endpoint: string }) {
  const [record, setRecord] = createSignal<PublicRecord | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");

  onMount(async () => {
    try {
      setRecord(await apiRequest<PublicRecord>(props.endpoint));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Record unavailable");
    } finally {
      setLoading(false);
    }
  });

  return (
    <main class="dump-root flex min-h-screen w-full items-center justify-center overflow-y-auto px-4 py-10 md:px-14">
      <section class="w-full max-w-3xl border border-white-11 bg-gray-gradient shadow-green-gradient">
        <header class="flex items-center justify-between border-b border-white-11 px-6 py-5">
          <div>
            <p class="roboto700 text-2xl text-cyan">NoPixel MDT</p>
            <p class="text-sm text-white-55">Public record</p>
          </div>
          <img
            class="h-12 w-12 object-contain"
            src={`${runtimeConfig.assetOrigin}/logo.png`}
            alt=""
          />
        </header>

        <Show
          when={!loading()}
          fallback={<p class="p-8 text-white-55">Loading record...</p>}
        >
          <Show
            when={record()}
            fallback={
              <div class="p-8">
                <p class="text-lg text-red-300">This public record is unavailable.</p>
                <p class="mt-2 text-sm text-white-55">
                  {error() || "The link may have expired or been revoked."}
                </p>
              </div>
            }
          >
            {(item) => (
              <article class="space-y-6 p-6 md:p-8">
                <div class="border-b border-white-11 pb-5">
                  <p class="text-xs uppercase tracking-[0.2em] text-white-55">
                    {item().reportType === "incident" ? "Incident" : "Report"}
                  </p>
                  <h1 class="mt-2 text-3xl text-white">{item().title}</h1>
                  <p class="mt-2 text-sm text-white-55">
                    Record #{item().reportId} - {formatDate(item().createdAt)}
                  </p>
                </div>

                <div>
                  <p class="mb-2 text-xs uppercase tracking-[0.2em] text-cyan">
                    Description
                  </p>
                  <p class="whitespace-pre-wrap leading-7 text-white-87">
                    {item().description || "No description provided."}
                  </p>
                </div>

                <Show when={item().subject}>
                  {(subject) => (
                    <div class="border-t border-white-11 pt-5">
                      <p class="mb-3 text-xs uppercase tracking-[0.2em] text-cyan">
                        Public subject
                      </p>
                      <p class="text-lg text-white">{subject().name}</p>
                      <p class="text-sm text-white-55">
                        State ID #{subject().id} - DOB {subject().dateOfBirth}
                      </p>
                    </div>
                  )}
                </Show>

                <p class="border-t border-white-11 pt-4 text-xs text-white-55">
                  This page contains only information intentionally released for public
                  viewing.
                  {item().expiresAt
                    ? ` Link expires ${formatDate(item().expiresAt!)}`
                    : ""}
                </p>
              </article>
            )}
          </Show>
        </Show>
      </section>
    </main>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
